import { Button, Upload, message }   from 'antd';
import { Col }                       from 'antd';
import { Form }                      from 'antd';
import { InboxOutlined }             from '@ant-design/icons';
import FormattedMessageWrapper       from 'components/App/FormattedMessageWrapper';
import Input                         from 'components/App/IntlInput';
import RichTextEditor                from 'components/App/RichTextEditor';
import { InputNumber }               from 'antd';
import MountingAttestationMediaModel from 'models/MountingAttestationMediaModel';
import MountingAttestationModel      from 'models/MountingAttestationModel';
import { Radio }                     from 'antd';
import React                         from 'react';
import Row                           from 'components/App/Row';
import { Select }                    from 'antd';
import SignatureCanvas               from 'react-signature-canvas';
import StandistByStandistModel       from 'models/StandistByStandistModel';
import { Steps }                     from 'antd';
import YesOrNo                       from 'components/App/YesOrNo';
import handleApiError                from 'utils/handleApiError';
import handleFormError               from 'utils/handleFormError';
import { formatMessageIntl }         from 'utils/messageIntl';
import { ApiCollection }             from 'collections/ApiCollection';
import { AppFormComponent }          from 'components/App/AppForm';
import type { FormInstance }         from 'antd';
import { ROLE_STANDIST_DECORATOR }   from 'constants/StandistRoleReference';
import { ROLE_STANDIST_EXHIBITOR }   from 'constants/StandistRoleReference';
import type { RefObject }            from 'react';
import { getStandistRoleIntlLabel }  from 'constants/StandistRoleReference';
import { observer }                  from 'mobx-react';
import type StandModel               from 'models/StandModel';
import UserModel                     from 'models/UserModel';

interface MontageAttestationFormProps {
    model: MountingAttestationModel;
    onSuccess?: (model: MountingAttestationModel) => void;
    stand: StandModel;
}

interface MontageAttestationFormState {
    addUser: boolean,
    assignedTo?: string,
    currentStep: number,
    fileList: any[],
    isLoading: boolean,
    userCreating: boolean,
}

class MountingAttestationForm extends AppFormComponent<MontageAttestationFormProps> {
    public collMountingAttestionMedia = new ApiCollection(MountingAttestationMediaModel);
    public collUser = new ApiCollection(UserModel);
    public formRef: RefObject<FormInstance> = React.createRef();
    public signatureCanvasRef: RefObject<SignatureCanvas> = React.createRef();

    public state: MontageAttestationFormState = {
        addUser: false,
        assignedTo: undefined,
        currentStep: 0,
        fileList: [],
        isLoading: false,
        userCreating: false,
    };

    public componentDidMount() {
        const {
            model,
            setModalFooter,
            stand,
        } = this.props;

        setModalFooter(this.getModalFooter());

        this.formRef.current?.setFieldsValue({
            addUser: false,
            anchoring: model.anchoring ?? false,
            assignedTo: model.assignedTo,
            ballasting: model.ballasting ?? false,
            brand: model.brand,
            categoryNormalUsage: model.categoryNormalUsage || undefined,
            categorySupportThePublic: model.categorySupportThePublic || undefined,
            electricalPowerKva: model.electricalPowerKva,
            equipmentDescription: model.equipmentDescription,
            groundHeightCategory: model.groundHeightCategory || 'inf_350',
            guying: model.guying ?? false,
            hitchType: model.hitchType || undefined,
            hitchTypeOther: model.hitchTypeOther,
            installedBy: model.installedBy,
            manufacturer: model.manufacturer,
            selfSupporting: model.selfSupporting ?? false,
            summaryDescription: model.summaryDescription,
            suspendedEquipmentSecured: model.suspendedEquipmentSecured ?? false,
            weight: model.weight,
            windSpeedLimit: model.windSpeedLimit,
        });

        if (model.id) {
            this.collMountingAttestionMedia
                .setFilter('mountingAttestation', model.id)
                // On récupère tous les médias, pas seulement les signatures
                .list()
                .then(() => {
                    const others = this.collMountingAttestionMedia
                        .filter(m => m.get('type') === 'other')
                        .map(m => ({
                            name: m.get('mediaFileName'),
                            status: 'done',
                            uid: m.get('id'),
                            url: m.get('objectUrl'),
                        }));
                    this.setState({ fileList: others });
                });
        }

        this.collUser
            .setFilter('userStandRoles.stand', stand.id)
            .list();

        // Redimensionnement initial après un court délai pour laisser le temps au layout de se stabiliser
        setTimeout(this.resizeCanvas, 50);
        window.addEventListener('resize', this.resizeCanvas);
    }

    public componentDidUpdate(_: MontageAttestationFormProps, prevState: MontageAttestationFormState) {
        if (
          prevState.currentStep !== this.state.currentStep
          || prevState.assignedTo !== this.state.assignedTo
          || prevState.isLoading !== this.state.isLoading
        ) {
            this.props.setModalFooter(this.getModalFooter());

            setTimeout(this.resizeCanvas, 100);
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.resizeCanvas);
    }

    public getModalFooter = () => {
        if (this.state.currentStep === 0) {
            return [
                <Button
                    key="back"
                    onClick={() => this.props.closeModal()}
                >
                    <FormattedMessageWrapper id="Annuler" />
                </Button>,
                <Button
                    disabled={!this.state.assignedTo}
                    key="next"
                    loading={this.state.userCreating}
                    onClick={() => {
                        this.setState({ currentStep: 1 });
                    }}
                >
                    <FormattedMessageWrapper id="Suivant" />
                </Button>,
            ];
        } else {
            return [
                <Button
                    key="back"
                    loading={this.state.isLoading}
                    onClick={() => this.props.closeModal()}
                >
                    <FormattedMessageWrapper id="Annuler" />
                </Button>,
                <Button
                    key="prev"
                    loading={this.state.isLoading}
                    onClick={() => {
                        this.setState({ currentStep: 0 });
                    }}
                >
                    <FormattedMessageWrapper id="Précédent" />
                </Button>,
                <Button
                    key="submit"
                    loading={this.state.isLoading}
                    onClick={() => {
                        this.submit();
                    }}
                    type="primary"
                >
                    <FormattedMessageWrapper id="Enregistrer" />
                </Button>
            ];
        }
    };

    public render() {
        const {
            stand,
        } = this.props;

        return (
            <Form
                layout="vertical"
                ref={this.formRef}
            >
                <Row gutter={[16, 32]}>
                    <Col span={24}>
                        <Steps
                            current={this.state.currentStep}
                            onChange={(current) => this.setState({ currentStep: current })}
                            items={[
                              { title: <FormattedMessageWrapper id="Responsable de l'attestation de bon montage" /> },
                              { title: <FormattedMessageWrapper id="Remplissage de l'attestation de bon montage" /> },
                            ]}
                        />
                    </Col>
                </Row>

                <br />

                <div style={{ display: this.state.currentStep === 0 ? 'block' : 'none' }}>
                    <Row gutter={[32, 32]}>
                        <Col md={12} sm={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Responsable" />}
                                labelCol={{ span: 24 }}
                                name="assignedTo"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Information manquante" />,
                                        required: true,
                                    },
                                ]}
                            >
                                <Select
                                    disabled={this.state.addUser}
                                    onChange={(value) => {
                                        this.setState({
                                            addUser: false,
                                            assignedTo: value,
                                        });
                                    }}
                                    size="large"
                                >
                                    {this.collUser.map((user: UserModel) => (
                                        <Select.Option
                                            key={user.id}
                                            value={user.iri}
                                        >
                                            {user.fullName} ({user.email})
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={12} sm={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Ajouter un utilisateur" />}
                                labelCol={{ span: 24 }}
                                name="addUser"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Information manquante" />,
                                        required: true,
                                    },
                                ]}
                            >
                                <YesOrNo
                                    onChange={(event) => {
                                        this.setState({ addUser: event.target.value });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col
                            span={24}
                            style={{
                                display: !this.state.addUser ? 'none' : 'block'
                            }}
                        >
                            <Row gutter={[32, 16]}>
                                <Col md={12} sm={24}>
                                    <Form.Item
                                        label={<FormattedMessageWrapper id="Société" />}
                                        labelCol={{ span: 24 }}
                                        name="company"
                                        rules={[
                                            {
                                                message: <FormattedMessageWrapper id="Société manquante" />,
                                                required: true,
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={[32, 8]}>
                                <Col md={12} sm={24}>
                                    <Form.Item
                                        label={<FormattedMessageWrapper id="Nom" />}
                                        labelCol={{ span: 24 }}
                                        name="lastName"
                                        rules={[
                                            {
                                                message: <FormattedMessageWrapper id="Nom manquant" />,
                                                required: true,
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col md={12} sm={24}>
                                    <Form.Item
                                        label={<FormattedMessageWrapper id="Prénom" />}
                                        labelCol={{ span: 24 }}
                                        name="firstName"
                                        rules={[
                                            {
                                                message: <FormattedMessageWrapper id="Prénom manquant" />,
                                                required: true,
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col md={12} sm={24}>
                                    <Form.Item
                                        label={<FormattedMessageWrapper id="Email" />}
                                        labelCol={{ span: 24 }}
                                        name="email"
                                        rules={[
                                            {
                                                message: <FormattedMessageWrapper id="Email manquant" />,
                                                required: true,
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col md={12} sm={24}>
                                    <Form.Item
                                        label={<FormattedMessageWrapper id="Téléphone" />}
                                        labelCol={{ span: 24 }}
                                        name="phone"
                                        rules={[
                                            {
                                                message: <FormattedMessageWrapper id="Téléphone manquant" />,
                                                required: true,
                                            },
                                        ]}
                                    >
                                        <Input autoComplete="off" />
                                    </Form.Item>
                                </Col>
                                <Col md={12} sm={24}>
                                    <Form.Item
                                        label={<FormattedMessageWrapper id="Exposant/Décorateur" />}
                                        labelCol={{ span: 24 }}
                                        name="standistRole"
                                        rules={[
                                            {
                                                message: <FormattedMessageWrapper id="Information manquante" />,
                                                required: true,
                                            },
                                        ]}
                                    >
                                        <Radio.Group>
                                            <Radio.Button
                                                key={ROLE_STANDIST_EXHIBITOR}
                                                value={ROLE_STANDIST_EXHIBITOR}
                                            >
                                                {getStandistRoleIntlLabel(ROLE_STANDIST_EXHIBITOR)}
                                            </Radio.Button>
                                            <Radio.Button
                                                key={ROLE_STANDIST_DECORATOR}
                                                value={ROLE_STANDIST_DECORATOR}
                                            >
                                                {getStandistRoleIntlLabel(ROLE_STANDIST_DECORATOR)}
                                            </Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                                <Col md={12} sm={24}>
                                    <Form.Item
                                        label=" "
                                        labelCol={{ span: 24 }}
                                    >
                                        <Button
                                            loading={this.state.userCreating}
                                            onClick={async () => {
                                                this.setState({ userCreating: true });

                                                try {
                                                    const values = await this.formRef.current?.validateFields(['company', 'firstName', 'lastName', 'email', 'phone', 'standistRole']);

                                                    const standistByStandist = new StandistByStandistModel();

                                                    await standistByStandist.patch({
                                                        company: values.company,
                                                        email: values.email,
                                                        firstName: values.firstName,
                                                        lastName: values.lastName,
                                                        phone: values.phone,
                                                        stand: stand?.iri,
                                                        standistRole: values.standistRole,
                                                    });

                                                    await this.collUser.list();

                                                    this.formRef.current?.setFieldsValue({
                                                        'addUser': false,
                                                        'assignedTo': standistByStandist.user, // iri de l'utilisateur
                                                    });

                                                    this.setState({
                                                        addUser: false,
                                                        assignedTo: standistByStandist.user,
                                                    });
                                                } catch (error: any) {
                                                    if (error.connectorError) {
                                                        handleApiError(error);
                                                    }
                                                } finally {
                                                    this.setState({ userCreating: false });
                                                }
                                            }}
                                            type="primary"
                                        >
                                            <FormattedMessageWrapper id="Ajouter" />
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>

                <div style={{ display: this.state.currentStep === 1 ? 'block' : 'none' }}>
                    <Row gutter={[32, 8]}>
                        <Col span={24}>
                            <b>
                                <FormattedMessageWrapper id="Caractéristique de l'ensemble démontable" />
                            </b>
                        </Col>
                        {/* Section Caractéristiques */}
                        <Col span={12}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Fabriqué par" />}
                                name="manufacturer"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: false,
                                    },
                                ]}
                            >
                                <Input size="large" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Marque" />}
                                name="brand"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: false,
                                    },
                                ]}
                            >
                                <Input size="large" />
                            </Form.Item>
                        </Col>

                        {/* Section Catégories */}
                        <Col span={24}>
                            <b>
                                <FormattedMessageWrapper id="Catégorie de l'ensemble démontable" />
                            </b>
                        </Col>
                        <Col md={12} sm={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Ossatures destinées à supporter du public (OP)" />}
                                name="categorySupportThePublic"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: false,
                                    },
                                ]}
                            >
                                <Select
                                    allowClear={true}
                                    size="large"
                                >
                                    <Select.Option value="OP1">OP1</Select.Option>
                                    <Select.Option value="OP2">OP2</Select.Option>
                                    <Select.Option value="OP3">OP3</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col md={12} sm={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Ossatures destinées à un usage normal (OS)" />}
                                name="categoryNormalUsage"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: false,
                                    },
                                ]}
                            >
                                <Select
                                    allowClear={true}
                                    size="large"
                                >
                                    <Select.Option value="OS1">OS1</Select.Option>
                                    <Select.Option value="OS2">OS2</Select.Option>
                                    <Select.Option value="OS3">OS3</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Construction au sol" />}
                                name="groundHeightCategory"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: true,
                                    },
                                ]}
                            >
                                <Radio.Group size="large">
                                    <Radio value="inf_350"><FormattedMessageWrapper id="Hauteur inférieure à 3.50m" /></Radio>
                                    <Radio value="sup_350"><FormattedMessageWrapper id="Hauteur supérieure à 3.50m" /></Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Descriptif sommaire" />}
                                name="summaryDescription"
                            >
                                <RichTextEditor style={{ height: 100, marginBottom: 32 }} />
                            </Form.Item>
                        </Col>

                        {/* Stabilité - Grid 4 colonnes */}
                        <Col span={6}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Ancrage" />}
                                name="anchoring"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: true,
                                    },
                                ]}
                            >
                                <YesOrNo />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Haubenage" />}
                                name="guying"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: true,
                                    },
                                ]}
                            >
                                <YesOrNo />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Lestage" />}
                                name="ballasting"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: true,
                                    },
                                ]}
                            >
                                <YesOrNo />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Autoporté" />}
                                name="selfSupporting"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: true,
                                    },
                                ]}
                            >
                                <YesOrNo />
                            </Form.Item>
                        </Col>

                        {/* Technique */}
                        <Col span={12}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Si installation extérieure, vitesse de vent retenue en km/h" />}
                                name="windSpeedLimit"
                                rules={[
                                    {
                                        type: 'number',
                                    }
                                ]}
                            >
                                <InputNumber
                                    size="large"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Puissance électrique (kVA)" />}
                                name="electricalPowerKva"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: false,
                                    },
                                    {
                                        type: 'number',
                                    },

                                ]}
                            >
                                <InputNumber
                                    max={999999}
                                    min={1}
                                    size="large"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Sécurisation équipements suspendus" />}
                                name="suspendedEquipmentSecured"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: true,
                                    },
                                ]}
                            >
                                <YesOrNo />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Descriptif sommaire" />}
                                name="equipmentDescription"
                            >
                                <RichTextEditor style={{ height: 100, marginBottom: 32 }} />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Poids (kg)" />}
                                name="weight"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: false,
                                    },
                                    {
                                        type: 'number',
                                    },
                                ]}
                            >
                                <InputNumber
                                    size="large"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>

                        <Col md={12} sm={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Type d'accroche" />}
                                name="hitchType"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: false,
                                    },
                                ]}
                            >
                                <Select
                                    allowClear
                                    mode="multiple"
                                    showSearch={false}
                                    size="large"
                                >
                                    <Select.Option value="sling">
                                        <FormattedMessageWrapper id="Elingues" />
                                    </Select.Option>
                                    <Select.Option value="halyard">
                                        <FormattedMessageWrapper id="Drisse" />
                                    </Select.Option>
                                    <Select.Option value="manual_hoist">
                                        <FormattedMessageWrapper id ="Palants manuel" />
                                    </Select.Option>
                                    <Select.Option value="motor_hoist">
                                        <FormattedMessageWrapper id="Palants moteur" />
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col md={12} sm={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Autre" />}
                                name="hitchTypeOther"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: false,
                                    },
                                ]}
                            >
                                <Input size="large" />
                            </Form.Item>
                        </Col>

                        <Col md={12} sm={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Installé par" />}
                                name="installedBy"
                            >
                                <Input size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[32, 8]}>
                        <Col span={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Pièces jointes" />}
                            >
                                <Upload.Dragger
                                    accept="application/pdf,image/*"
                                    beforeUpload={(file) => {
                                        const isPdfOrImage = file.type === 'application/pdf' || file.type.startsWith('image/');
                                        if (!isPdfOrImage) {
                                            message.error(formatMessageIntl("Vous ne pouvez envoyer que des fichiers PDF ou Image !"));
                                        }
                                        return false;
                                    }}
                                    fileList={this.state.fileList}
                                    multiple={true}
                                    onChange={(info) => {
                                        this.setState({ fileList: info.fileList });
                                    }}
                                >
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined />
                                    </p>
                                    <p className="ant-upload-text">
                                        <FormattedMessageWrapper id="Cliquez ou glissez des fichiers dans cette zone pour les ajouter" />
                                    </p>
                                </Upload.Dragger>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[32, 8]}>
                        <Col span={24}>
                            <Form.Item
                                label={<FormattedMessageWrapper id="Signature" />}
                                name="signature"
                                rules={[
                                    {
                                        message: <FormattedMessageWrapper id="Le champ est requis" />,
                                        required: true,
                                    },
                                ]}
                            >
                                <div
                                    style={{
                                        background: '#fafafa',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 2,
                                    }}
                                >
                                    <SignatureCanvas
                                        canvasProps={{
                                            className: 'sigCanvas',
                                            height: 200,
                                            style: {
                                                height: 200,
                                                width: '100%',
                                            },
                                            // width: 500,
                                        }}
                                        penColor="black"
                                        ref={this.signatureCanvasRef}
                                    />
                                </div>
                                <br />
                                <Button
                                    onClick={() => this.signatureCanvasRef.current?.clear()}
                                    size="large"
                                >
                                    <FormattedMessageWrapper id="Effacer la signature" />
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </Form>
        );
    }

    public submit = async () => {
        const { model, onSuccess, stand } = this.props;

        this.setState({ isLoading: true });

        try {
            const values = await this.formRef.current?.validateFields([
                'assignedTo', 'manufacturer', 'brand',
                'categorySupportThePublic', 'categoryNormalUsage', 'groundHeightCategory', 'summaryDescription',
                'anchoring', 'guying', 'ballasting', 'selfSupporting', 'windSpeedLimit', 'electricalPowerKva',
                'suspendedEquipmentSecured', 'equipmentDescription', 'weight', 'hitchType', 'hitchTypeOther', 'installedBy',
            ]);

            await model.patch({
                ...values,
                stand: stand.iri,
            });

            // Gérer les médias de type 'other'
            const currentOtherMediaIds = this.state.fileList
                .filter(file => !file.originFileObj)
                .map(file => file.uid);

            const mediasToDelete = this.collMountingAttestionMedia
                .filter(
                  m => m.get('type') === 'other'
                    && !currentOtherMediaIds.includes(m.get('id')));

            for (const media of mediasToDelete) {
                await media.destroy();
            }

            // Post other media files
            for (const fileItem of this.state.fileList) {
                if (fileItem.originFileObj) {
                    const isPdfOrImage = fileItem.originFileObj.type === 'application/pdf'
                      || fileItem.originFileObj.type.startsWith('image/');
                    if (isPdfOrImage) {
                        const mediaModel = new MountingAttestationMediaModel();
                        mediaModel.set({
                            file: fileItem.originFileObj,
                            mountingAttestation: model.iri,
                            type: 'other',
                        });
                        await mediaModel.save();
                    }
                }
            }

            const signatureFile = await this.getSignatureFile();
            if (signatureFile) {
                const prevSignature = this.collMountingAttestionMedia.find(
                  m => m.get('type') === 'signature'
                );
                if (prevSignature) {
                    await prevSignature.destroy();
                }

                const mediaModel = new MountingAttestationMediaModel();
                mediaModel.set({
                    file: signatureFile,
                    mountingAttestation: model.iri,
                    type: 'signature',
                });
                await mediaModel.save();
            }

            await model.patch({
                status: 'completed',
            });

            if (onSuccess) {
                onSuccess(model);
            }
        } catch (error) {
            handleFormError(error, this.formRef.current);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private getSignatureFile = async (): Promise<File | null> => {
        const signatureCanvas = this.signatureCanvasRef.current;
        if (!signatureCanvas || signatureCanvas.isEmpty()) {
            return null;
        }

        // const dataUrl = signatureCanvas.getTrimmedCanvas().toDataURL('image/png');

        // Au lieu de getTrimmedCanvas(), on prend le canvas brut
        // et on le "trim" nous-mêmes si nécessaire, ou on l'envoie tel quel.
        const rawCanvas = signatureCanvas.getCanvas();
        const dataUrl = rawCanvas.toDataURL('image/png');

        const res = await fetch(dataUrl);
        const blob = await res.blob();

        return new File([blob], 'signature.png', { type: 'image/png' });
    };

    private resizeCanvas = () => {
        const canvas = this.signatureCanvasRef.current?.getCanvas();
        const signaturePad = this.signatureCanvasRef.current; // Référence au composant

        if (canvas && signaturePad) {
            // 1. Sauvegarder la signature actuelle
            const data = signaturePad.toData();

            // 2. Calculer le nouveau ratio et dimensions
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            const width = canvas.offsetWidth;
            const height = canvas.offsetHeight;

            // 3. Mettre à jour les dimensions (cela efface le canvas)
            canvas.width = width * ratio;
            canvas.height = height * ratio;
            canvas.getContext('2d')?.scale(ratio, ratio);

            // 4. Restaurer la signature
            // fromData s'occupe de redessiner les points sauvegardés
            signaturePad.fromData(data);
        }
    };
}

const MountingAttestationFormObserver = observer(MountingAttestationForm);

export default MountingAttestationFormObserver;
